import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Select, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { RunnerAction } from '../components/RunnerAction';
import { RunnerPageHeading } from '../components/RunnerPageHeading';
import { Panel } from '../components/Panel';
import { TimeOffPanel } from '../components/TimeOffPanel';
import { LimitsPanel } from '../components/LimitsPanel';
import { runnerApi, RunnerApiError, type AvailabilityWindow, type DayOfWeek, type MyLimits } from '../lib/runnerApiClient';

/**
 * §6 — the Runner's offered recurring DELIVERY windows.
 *
 * The copy says "delivery window" everywhere and never "shift" or "start time".
 * What a Runner offers is the slot the CUSTOMER is promised; the pickup time is
 * derived server-side from vendor prep and the cutoff. Calling it anything else
 * invites a Runner to enter when they plan to leave home, which would shift
 * every customer's promised slot without anyone noticing.
 */

const DAYS: { value: DayOfWeek; key: string }[] = [
  { value: 0, key: 'sun' },
  { value: 1, key: 'mon' },
  { value: 2, key: 'tue' },
  { value: 3, key: 'wed' },
  { value: 4, key: 'thu' },
  { value: 5, key: 'fri' },
  { value: 6, key: 'sat' },
];

/** 09:00 -> 9:00 AM, in the viewer's locale rather than a hand-rolled table. */
function prettyTime(hhmm: string, locale: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(Date.UTC(1970, 0, 1, h, m));
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(d);
}

function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A window is live if it has started and has not been ended. */
function isLive(w: AvailabilityWindow): boolean {
  const today = todayYmd();
  return w.effectiveFrom <= today && (w.effectiveTo === null || w.effectiveTo >= today);
}

const EMPTY_DRAFT = {
  dayOfWeek: 1 as DayOfWeek,
  deliveryWindowStart: '09:00',
  deliveryWindowEnd: '12:00',
  zone: '',
  capacityDeliveries: 4,
};

export default function Schedule() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  const [windows, setWindows] = useState<AvailabilityWindow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  // Re-mounts the limits panel after a window changes, so the meter is live.
  const [limitsKey, setLimitsKey] = useState(0);
  const [limits, setLimits] = useState<MyLimits | null>(null);

  const load = useCallback(async () => {
    try {
      setLoadError(null);
      const { windows: rows } = await runnerApi.availability();
      setWindows(rows);
    } catch (e) {
      setWindows([]);
      setLoadError(e instanceof RunnerApiError ? e.message : t('schedule.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const live = useMemo(() => (windows ?? []).filter(isLive), [windows]);
  const ended = useMemo(() => (windows ?? []).filter((w) => !isLive(w)), [windows]);

  const weeklyCapacity = useMemo(() => live.reduce((n, w) => n + w.capacityDeliveries, 0), [live]);
  // The ceiling is the server's; this is the early message, the 409 is the rule.
  const ceiling = limits ? limits.limits.deliveries : null;
  const overWithDraft = ceiling !== null && weeklyCapacity + draft.capacityDeliveries > ceiling;
  const capacityLimitHint = ceiling === null
    ? t('schedule.limitHint_none')
    : overWithDraft
      ? t('schedule.limitOver', { limit: ceiling })
      : weeklyCapacity + draft.capacityDeliveries === ceiling
        ? t('schedule.limitAt', { limit: ceiling })
        : t('schedule.limitHint', { left: ceiling - weeklyCapacity - draft.capacityDeliveries, limit: ceiling });

  async function submitDraft() {
    setBusy(true);
    setFormError(null);
    try {
      await runnerApi.createAvailability({ ...draft, zone: draft.zone.trim() });
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      await load();
      setLimitsKey((k) => k + 1);
    } catch (e) {
      // A 409 here is the server's overlap guard and its message is written to
      // be read by a Runner — surface it verbatim rather than replacing it with
      // something vaguer.
      setFormError(e instanceof RunnerApiError ? e.message : t('schedule.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function endWindow(w: AvailabilityWindow) {
    setBusy(true);
    setFormError(null);
    try {
      await runnerApi.endAvailability(w.id);
      await load();
      setLimitsKey((k) => k + 1);
    } catch (e) {
      setFormError(e instanceof RunnerApiError ? e.message : t('schedule.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (windows === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box data-testid="page-schedule">
      <RunnerPageHeading title={i18n.language.startsWith('es') ? 'Mi horario' : 'My Schedule'} subtitle={t('schedule.subtitle')} topic="schedule" onRefresh={() => void load()} />

      {loadError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" data-testid="schedule-load-error">{loadError}</Alert>
        </Box>
      )}

      {/* The ceiling first: a Runner adding a window should see the number it counts against (Rod 2026-09-10). */}
      <Box sx={{ mb: 2.5 }}>
        <LimitsPanel key={limitsKey} onLoaded={setLimits} />
      </Box>

      <Panel title={t('schedule.liveTitle')} dataTestId="schedule-live">
        {live.length === 0 ? (
          // Truthful empty state: say what is true for customers right now, not
          // an encouraging blank.
          <Typography data-testid="schedule-empty" sx={{ color: '#5f6b7a', fontSize: '14px' }}>{t('schedule.empty')}</Typography>
        ) : (
          <>
            <Box sx={{ display: 'grid', gap: 1 }}>
              {live.map((w) => (
                <Box
                  key={w.id}
                  data-testid={`schedule-window-${w.id}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    border: '1px solid #e5e7eb',
                    borderRadius: 2,
                    px: 1.75,
                    py: 1.25,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>
                      {t(`schedule.day.${DAYS.find((d) => d.value === w.dayOfWeek)?.key}`)}{' '}
                      {prettyTime(w.deliveryWindowStart, locale)} – {prettyTime(w.deliveryWindowEnd, locale)}
                    </Typography>
                    <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>
                      {t('schedule.zoneLabel')} {w.zone} ·{' '}
                      {t('schedule.capacityCount', { count: w.capacityDeliveries })}
                    </Typography>
                  </Box>
                  <RunnerAction topic="schedule"
                    ariaLabel={t('schedule.end')}
                    dataTestId={`schedule-window-end-${w.id}`}
                    buttonText={t('schedule.end')}

                    size="small"
                    onClick={() => void endWindow(w)}
                    disabled={busy}
                  />
                </Box>
              ))}
            </Box>
            <Typography data-testid="schedule-weekly-total" sx={{ color: ceiling !== null && weeklyCapacity >= ceiling ? '#b42318' : '#5f6b7a', fontSize: '13px', mt: 1.5, fontWeight: ceiling !== null && weeklyCapacity >= ceiling ? 700 : 400 }}>
              {ceiling === null ? t('schedule.weeklyTotal', { count: weeklyCapacity }) : t('schedule.weeklyTotalOf', { count: weeklyCapacity, limit: ceiling })}
            </Typography>
          </>
        )}
      </Panel>

      <Box sx={{ mt: 2.5 }}>
        <Panel title={t('schedule.addTitle')} dataTestId="schedule-add-panel">
          {formError && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error" data-testid="schedule-form-error">{formError}</Alert>
            </Box>
          )}

          {!adding ? (
            <RunnerAction topic="schedule"
              ariaLabel={t('schedule.add')}
              dataTestId="schedule-add"
              buttonText={t('schedule.add')}
              appearance="primary"
              size="small"
              onClick={() => setAdding(true)}
              disabled={busy}
            />
          ) : (
            <>
              {/* 🪤 Each field is wrapped in its own Box: DSL TextField below
                  3.0.1 renders label and input as SIBLINGS, so a bare field in
                  a grid eats two cells and labels drift beside the wrong input. */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                  gap: 2,
                }}
              >
                <Box>
                  <Select
                    ariaLabel={t('schedule.dayLabel')}
                    data-testid="schedule-day"
                    label={t('schedule.dayLabel')}
                    native
                    size="small"
                    value={String(draft.dayOfWeek)}
                    onChange={(_e, val) => setDraft({ ...draft, dayOfWeek: Number(val) as DayOfWeek })}
                    options={DAYS.map((d) => ({ value: String(d.value), label: t(`schedule.day.${d.key}`) }))}
                    sx={{ width: '100%' }}
                  />
                </Box>
                <Box>
                  <TextField
                    label={t('schedule.startLabel')}
                    ariaLabel={t('schedule.startLabel')}
                    dataTestId="schedule-start"
                    size="small"
                    type="time"
                    fullWidth
                    value={draft.deliveryWindowStart}
                    disabled={busy}
                    onChange={(e) => setDraft({ ...draft, deliveryWindowStart: e.target.value })}
                  />
                </Box>
                <Box>
                  <TextField
                    label={t('schedule.endLabel')}
                    ariaLabel={t('schedule.endLabel')}
                    dataTestId="schedule-end-time"
                    size="small"
                    type="time"
                    fullWidth
                    value={draft.deliveryWindowEnd}
                    disabled={busy}
                    onChange={(e) => setDraft({ ...draft, deliveryWindowEnd: e.target.value })}
                  />
                </Box>
                <Box>
                  <TextField
                    label={t('schedule.zoneLabel')}
                    ariaLabel={t('schedule.zoneLabel')}
                    dataTestId="schedule-zone"
                    size="small"
                    fullWidth
                    value={draft.zone}
                    disabled={busy}
                    placeholder="28202"
                    onChange={(e) => setDraft({ ...draft, zone: e.target.value })}
                  />
                </Box>
                <Box>
                  <TextField
                    label={t('schedule.capacityLabel')}
                    ariaLabel={t('schedule.capacityLabel')}
                    dataTestId="schedule-capacity"
                    size="small"
                    type="number"
                    fullWidth
                    value={String(draft.capacityDeliveries)}
                    disabled={busy}
                    onChange={(e) => setDraft({ ...draft, capacityDeliveries: Math.max(0, Number(e.target.value) || 0) })}
                  />
                </Box>
              </Box>
              <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mt: 1.5 }}>
                {t('schedule.capacityHint')}
              </Typography>
              <Typography data-testid={overWithDraft ? 'schedule-limit-over' : 'schedule-limit-hint'} sx={{ color: overWithDraft ? '#b42318' : '#5f6b7a', fontSize: '13px', mt: 0.5, fontWeight: overWithDraft ? 700 : 400 }}>
                {capacityLimitHint}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                <RunnerAction topic="schedule"
                  ariaLabel={t('schedule.save')}
                  dataTestId="schedule-save"
                  buttonText={t('schedule.save')}
                  appearance="primary"
                  size="small"
                  onClick={() => void submitDraft()}
                  disabled={busy || !draft.zone.trim() || overWithDraft}
                />
                <RunnerAction topic="schedule"
                  ariaLabel={t('schedule.cancel')}
                  dataTestId="schedule-cancel"
                  buttonText={t('schedule.cancel')}

                  size="small"
                  onClick={() => {
                    setAdding(false);
                    setDraft(EMPTY_DRAFT);
                    setFormError(null);
                  }}
                  disabled={busy}
                />
              </Box>
            </>
          )}
        </Panel>
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <TimeOffPanel />
      </Box>

      {ended.length > 0 && (
        <Box sx={{ mt: 2.5 }}>
          <Panel title={t('schedule.endedTitle')} accent="#9ca3af" dataTestId="schedule-ended">
            <Box sx={{ display: 'grid', gap: 0.75 }}>
              {ended.map((w) => (
                <Typography key={w.id} data-testid={`schedule-ended-${w.id}`} sx={{ color: '#5f6b7a', fontSize: '13px' }}>
                  {t(`schedule.day.${DAYS.find((d) => d.value === w.dayOfWeek)?.key}`)}{' '}
                  {prettyTime(w.deliveryWindowStart, locale)} – {prettyTime(w.deliveryWindowEnd, locale)} · {w.zone}
                  {w.effectiveTo ? ` · ${t('schedule.endedOn', { date: w.effectiveTo })}` : ''}
                </Typography>
              ))}
            </Box>
          </Panel>
        </Box>
      )}
    </Box>
  );
}
